import { Injectable, signal, computed } from '@angular/core';

export const enum Agents {
  mx = "unity_mx",
  fc = "unity_fc",
  fcPlus = "fc_plus",
  aura = "unity_aura",
  cs = "cs",
  onRoad = "on_road",
  onSite = "on_site"
}

declare global {
  interface Window {
    aptrinsic: any;
  }
}

@Injectable({ providedIn: 'root' })
export class EnvironmentService {
  private _apiUrl: string = "";
  private _isEmbedded: boolean = true;
  private _host: string = "standalone";
  private _gainsightPxKey: string = "";
  private _chatConfig: any = {
    showAgentHeader: false,
    initSideBarCollapsed: false,
    selectedAgent: undefined,
    allowToggleTempChat: true,
    initialSidebarCollapsed: true,
    showAgentSelector: true,
    showThemeSelector: true,
    showChatInputKbSources: true,
    showChatInputPreferences: true,
    showDeepResearch: false,
    showSmallModel: false,
    expandReferencesByDefault: false,
  };

  readonly #environment = signal<string>('dev');
  readonly isDev = computed<boolean>(() => this.#environment() === 'int.');

  public get apiUrl(): string {
    return this._apiUrl;
  }

  public get isEmbedded(): boolean {
    return this._isEmbedded;
  }

  public get host(): string {
    return this._host;
  }

  public get chatConfig(): any {
    return { ... this._chatConfig };
  }

  public get gainsightPxKey(): string {
    return this._gainsightPxKey;
  }

  public get buildNumber(): string {
    return "20261104.2.dev";
  }

  constructor() {
    console.debug("Aura app version:", this.buildNumber);
  }

  initialise(): Promise<void> {
    return new Promise(resolve => {
      const hostname = window?.location?.hostname || '';
      const pathname = window?.location?.pathname || '/';
      const search = window?.location?.search || '';
      this.setEnvironment(hostname, pathname, search);
      resolve();
    });
  }

  private setEnvironment(hostname: string, pathname: string, search: string): void {
    let envMatch: RegExpMatchArray | null;

    let configUnityEmbedded = {
      initialSidebarCollapsed: true,
      showAgentSelector: false,
      showThemeSelector: false,
      showChatInputKbSources: false,
      showChatInputPreferences: true,
      expandReferencesByDefault: false,
    }

    // resolve platform from path
    if (pathname.startsWith('/unity-mx')) {
      this._host = "unity-mx";
      this._isEmbedded = true;
      this._chatConfig = {
        ...this._chatConfig,
        ...configUnityEmbedded,
        selectedAgent: Agents.mx
      }
    }
    else if (pathname.startsWith('/mfm')) {
      this._host = "on-road";
      this._isEmbedded = true;
      this._chatConfig = {
        ...this._chatConfig,
        ...configUnityEmbedded,
        selectedAgent: Agents.onRoad
      }
    }
    else if (pathname.startsWith('/unity-fc')) {
      this._host = "unity-fc";
      this._isEmbedded = true;
      this._chatConfig = {
        ...this._chatConfig,
        ...configUnityEmbedded,
        selectedAgent: Agents.fc,
      }
    }
    else if (pathname.startsWith('/fc-plus')) {
      this._host = "fc-plus";
      this._isEmbedded = true;
      this._chatConfig = {
        ...this._chatConfig,
        ...configUnityEmbedded,
        selectedAgent: Agents.fcPlus,
      }
    }
    else if (pathname.startsWith('/vision-pro')) {
      this._host = "vision-pro";
      this._isEmbedded = true;
      this._chatConfig = {
        ...this._chatConfig,
        ...configUnityEmbedded,
        selectedAgent: Agents.onSite,
        showDeepResearch: false,
        showSmallModel: false,
      }
    }
    else if (pathname.startsWith('/seed')) {
      this._host = "seed";
      this._isEmbedded = true;
      this._chatConfig = {
        ...this._chatConfig,
        selectedAgent: Agents.aura,
        initialSidebarCollapsed: true,
        showAgentSelector: false,
        showThemeSelector: false,
        showChatInputKbSources: false,
        showChatInputPreferences: true,
        expandReferencesByDefault: false,
      }
    } else {
      this._host = "standalone";
      this._isEmbedded = false;
      this._chatConfig = {
        ...this._chatConfig,
        showAgentSelector: true,
      }
    }

    console.debug("Platform detected:", this._host);

    if (/^tech-support\./.test(hostname)) {
      this._chatConfig = {
        ...this._chatConfig,
        selectedAgent: Agents.cs,
        allowToggleTempChat: false,
        initialSidebarCollapsed: true,
        showAgentSelector: false,
        showChatInputKbSources: true,
        showChatInputPreferences: false,
        expandReferencesByDefault: true,
      }
    }

    // Resolve API URL

    // tech-support
    if (/^tech-support\./.test(hostname)) {
      this._apiUrl = `https://${hostname.replace('tech-support.', 'api.')}`;
    }
    // local
    else if (/^.*localhost.*/.test(hostname)) {
      if (this._host === "standalone") {
        // this._apiUrl = "http://localhost:8000";
        this._apiUrl = "https://api.int.aura.unityaiot.com";
      }
      else {
        // this._apiUrl = "http://localhost:8000";
        this._apiUrl = "https://api.int.ai-assistant.unityaiot.com";
      }
    }
    // aura.unityaiot.com or aura.powerfleet.com
    else if (envMatch = hostname.match(/^(?:([a-z]+)\.)?aura\.(unityaiot|powerfleet)\.com$/)) {
      const domain = envMatch[2]; // 'unityaiot' or 'powerfleet'
      if (this._isEmbedded) {
        this.#environment.set(envMatch[1] ? envMatch[1] + '.' : 'uk.'); // default to uk when no environment specified
        this._apiUrl = `https://api.${this.#environment()}ai-assistant.unityaiot.com`;
      }
      else {
        this.#environment.set(envMatch[1] ? envMatch[1] + '.' : '');
        this._apiUrl = `https://api.${this.#environment()}aura.${domain}.com`;
      }
    } else {
      console.error(`Environment configuration not found for hostname; [${hostname}]`);
    }

    // handle special case where Aura is embedded within MFM or env is passed via query param
    let envSearch = new URLSearchParams(search).get("env");
    let isOnroad = this._host === "on-road";
    if (envSearch !== null && (isOnroad || !["int.", "uat."].includes(this.#environment()))) {
      envSearch = envSearch.replace('za', 'uk').replace('ent', 'uk');
      this._apiUrl = `https://api.${envSearch}.ai-assistant.unityaiot.com`;
      this.#environment.set(envSearch + ".");
    }

    // override api url if set in local storage for int. environments
    if (this.#environment() === "int." || this.#environment() === "api.int.") {
      const apiUrl = localStorage.getItem("apiUrl");
      if (apiUrl) {
        this._apiUrl = apiUrl;
      }
    }

    // override selected agent if set in local storage (user will still need permissions to that agent)
    const selectedAgent = localStorage.getItem("selectedAgent");
    if (selectedAgent) {
      this._chatConfig = {
        ...this._chatConfig,
        selectedAgent: selectedAgent,
      }
    }

    // Resolve Gainsight PX key based on environment (FC+ is part of Unity FC)
    const isFc = this.host == "unity-fc" || this.host == "fc-plus";
    if (/^.*localhost.*/.test(hostname)) {
      this._gainsightPxKey = ''; // Do not load Gainsight on localhost
    } else if (/^(?:int\.)/.test(hostname) || this.#environment() === 'int.') {
      this._gainsightPxKey = isFc ? '' : 'AP-5PAYV3N1FZOG-2-4'; // Do not log for FC in INT
    } else if (/^(?:uat\.)/.test(hostname) || this.#environment() === 'uat.') {
      this._gainsightPxKey = isFc ? 'AP-CCVI70YW72DR-2-3' : 'AP-5PAYV3N1FZOG-2-3';
    } else {
      this._gainsightPxKey = isFc ? 'AP-CCVI70YW72DR-2' : 'AP-5PAYV3N1FZOG-2'; // Production
    }
  }
}
